import React, { useEffect, useState } from "react";
import axios from "@/lib/api";
import toast from "react-hot-toast";
import { AlertTriangle } from "lucide-react";

/* ---------- Inline Confirm Dialog (like your Notifications example) ---------- */
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
}
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-md mx-4 transition-all transform bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h3 className="ml-3 text-lg font-semibold text-red-600">{title}</h3>
          </div>

          <p className="mb-6 text-gray-600">{message}</p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-white transition-colors bg-red-500 rounded-lg hover:bg-red-600"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
/* --------------------------------------------------------------------------- */

interface Car {
  _id: string;
  name: string;
  model: string;
  engine: string;
  plate: string;
  status: string;
  image?: string;
  regionRates: {
    bt: number;
    eastern: number;
    central: number;
    northern: number;
  };
  booking?: {
    approved: boolean;
    dateBooked: string;
    returnDate: string;
  };
}

interface CarTableProps {
  filterStatus: string;
  search: string;
}

const CarTable: React.FC<CarTableProps> = ({ filterStatus, search }) => {
  const [carList, setCarList] = useState<Car[]>([]);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});

  // confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [carIdPendingDelete, setCarIdPendingDelete] = useState<string | null>(
    null
  );
  const [carNamePendingDelete, setCarNamePendingDelete] = useState<string>("");

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await axios.get("/cars");
        setCarList(Array.isArray(res.data) ? res.data : res.data?.data ?? []);
      } catch (err) {
        console.error("Failed to fetch cars", err);
        toast.error("Failed to load cars.");
      }
    };
    fetchCars();
  }, []);

  // Toggle repair/available
  const handleRepairToggle = async (carId: string, currentStatus: string) => {
    setLoading((prev) => ({ ...prev, [carId]: true }));
    try {
      const newStatus =
        currentStatus === "Under Repair" ? "Available" : "Under Repair";
      await axios.put(`/cars/${carId}/status`, { status: newStatus });

      setCarList((prevList) =>
        prevList.map((car) =>
          car._id === carId
            ? {
                ...car,
                status: newStatus,
                // backend clears booking when marking "Under Repair"; keep FE safe too
                booking: newStatus === "Under Repair" ? undefined : car.booking,
              }
            : car
        )
      );
      toast.success(
        newStatus === "Under Repair"
          ? "Car marked as under repair."
          : "Car marked as available."
      );
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "Failed to update car status";
      toast.error(msg);
    } finally {
      setLoading((prev) => ({ ...prev, [carId]: false }));
    }
  };

  // Open confirm dialog
  const askDelete = (carId: string, carName?: string) => {
    setCarIdPendingDelete(carId);
    setCarNamePendingDelete(carName || "");
    setConfirmOpen(true);
  };

  // Execute hard delete
  const doDelete = async () => {
    const carId = carIdPendingDelete;
    if (!carId) {
      setConfirmOpen(false);
      return;
    }

    setDeleting((prev) => ({ ...prev, [carId]: true }));
    setConfirmOpen(false);

    try {
      await axios.delete(`/cars/${carId}`);
      setCarList((list) => list.filter((c) => c._id !== carId));
      toast.success("Car deleted successfully.");
    } catch (err: any) {
      console.error("Delete failed", err);
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        (status === 401
          ? "Unauthorized. Please log in."
          : status === 403
          ? "Forbidden. Admin access required."
          : "Failed to delete car. Try again.");
      toast.error(msg);
    } finally {
      setDeleting((prev) => ({ ...prev, [carId]: false }));
      setCarIdPendingDelete(null);
      setCarNamePendingDelete("");
    }
  };

  const normalizedSearch = search.toLowerCase();
  const filteredCars = carList.filter((car) => {
    const matchesStatus = filterStatus === "All" || car.status === filterStatus;
    const matchesSearch =
      car.name.toLowerCase().includes(normalizedSearch) ||
      car.model.toLowerCase().includes(normalizedSearch) ||
      car.plate.toLowerCase().includes(normalizedSearch);
    return matchesStatus && matchesSearch;
  });

  const formatCurrency = (value?: number) =>
    value && value > 0 ? "MK " + value.toLocaleString("en-MW") : "—";

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Available":
        return "bg-green-100 text-green-700";
      case "Booked":
        return "bg-yellow-100 text-yellow-700";
      case "Under Repair":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const canToggleRepair = (status: string) =>
    status === "Available" || status === "Under Repair";

  return (
    <div className="p-4 overflow-x-auto bg-white rounded shadow">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-600">
        <span className="font-semibold text-blue-900">Region scheme:</span>
        <span className="px-2 py-0.5 rounded border border-blue-200 text-blue-900 bg-blue-50">
          Southern
        </span>
        <span className="px-2 py-0.5 rounded border border-amber-200 text-amber-800 bg-amber-50">
          Eastern
        </span>
        <span className="px-2 py-0.5 rounded border border-teal-200 text-teal-800 bg-teal-50">
          Central
        </span>
        <span className="px-2 py-0.5 rounded border border-indigo-200 text-indigo-800 bg-indigo-50">
          Northern
        </span>
        <span className="ml-2 text-gray-400">
          (/day, MWK — blank means not set)
        </span>
      </div>

      <table className="w-full text-sm text-left text-gray-700">
        <thead className="text-xs text-blue-900 uppercase border-b border-gray-200">
          <tr>
            <th className="px-4 py-2">Car</th>
            <th className="px-4 py-2">Model</th>
            <th className="px-4 py-2">Engine</th>
            <th className="px-4 py-2">Plate</th>
            <th className="px-4 py-2" title="Southern (legacy key: bt)">
              Southern
            </th>
            <th className="px-4 py-2">Eastern</th>
            <th className="px-4 py-2">Central</th>
            <th className="px-4 py-2">Northern</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Booking Info</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>

        <tbody>
          {filteredCars.length > 0 ? (
            filteredCars.map((car) => {
              const isBusy = Boolean(loading[car._id] || deleting[car._id]);
              return (
                <tr
                  key={car._id}
                  className="align-middle border-b border-gray-100"
                >
                  <td className="px-4 py-2 whitespace-nowrap">{car.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{car.model}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{car.engine}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{car.plate}</td>

                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatCurrency(car.regionRates?.bt)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatCurrency(car.regionRates?.eastern)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatCurrency(car.regionRates?.central)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatCurrency(car.regionRates?.northern)}
                  </td>

                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(
                        car.status
                      )} whitespace-nowrap`}
                    >
                      {car.status}
                    </span>
                  </td>

                  <td className="px-4 py-2">
                    {car.status === "Booked" && car.booking ? (
                      <div className="space-y-1 text-xs text-gray-700">
                        <p>
                          Status:{" "}
                          <span className="font-medium text-blue-900">
                            {car.booking.approved ? "Approved" : "Pending"}
                          </span>
                        </p>
                        <p>
                          Booked:{" "}
                          {new Date(
                            car.booking.dateBooked
                          ).toLocaleDateString()}
                        </p>
                        <p>
                          Return:{" "}
                          {new Date(
                            car.booking.returnDate
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {canToggleRepair(car.status) && (
                        <button
                          onClick={() =>
                            handleRepairToggle(car._id, car.status)
                          }
                          disabled={isBusy}
                          className={`inline-flex h-8 items-center justify-center whitespace-nowrap rounded px-3 text-xs font-medium transition-colors ${
                            car.status === "Under Repair"
                              ? "bg-green-100 text-green-700 hover:bg-green-200"
                              : "bg-red-100 text-red-700 hover:bg-red-200"
                          } ${
                            isBusy
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:shadow-sm"
                          }`}
                          title={
                            car.status === "Under Repair"
                              ? "Mark as Available"
                              : "Mark as Under Repair"
                          }
                        >
                          {loading[car._id]
                            ? "Loading..."
                            : car.status === "Under Repair"
                            ? "Mark Available"
                            : "Mark for Repair"}
                        </button>
                      )}

                      <button
                        onClick={() => askDelete(car._id, car.name)}
                        disabled={isBusy}
                        className={`inline-flex h-8 items-center justify-center whitespace-nowrap rounded px-3 text-xs font-medium transition-colors ${
                          deleting[car._id]
                            ? "bg-rose-300 cursor-not-allowed text-white"
                            : "bg-rose-600 text-white hover:bg-rose-500"
                        }`}
                        title="Delete car permanently"
                      >
                        {deleting[car._id] ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={11} className="px-4 py-4 text-center text-gray-400">
                No cars found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Confirm Delete Dialog (inline) */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setCarIdPendingDelete(null);
          setCarNamePendingDelete("");
        }}
        onConfirm={doDelete}
        title="Delete Car"
        message={
          carNamePendingDelete
            ? `Are you sure you want to permanently delete "${carNamePendingDelete}"? This action cannot be undone.`
            : "Are you sure you want to delete this car? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default CarTable;
